// Product free delivery bar
//
// Keeps the message in snippets/product-free-delivery.liquid true as the bag changes. The message
// is rendered server side, so this only has to answer for what happens after: adding something
// from the recommendations, or emptying the bag from the drawer, both of which move the order
// across the threshold without the product page reloading.
//
// The cart total is worked out here exactly as the Liquid works it out, and as the theme's own
// cart bar does — what is being paid for goods that ship, after any cart level discount — so the
// two can't drift apart.

// The placeholder the merchant writes in the message settings. Deliberately not Liquid's own
// syntax: these are plain text settings, and anything brace-shaped invites being read as a tag.
const AMOUNT_PLACEHOLDER = /\[amount\]/g;

function shippableTotal(cart) {
	const items = (cart.items || [])
		.filter((item) => item.requires_shipping)
		.reduce((total, item) => total + item.final_line_price, 0);

	const discount = (cart.cart_level_discount_applications || []).reduce(
		(total, application) => total + application.total_allocated_amount,
		0
	);

	return items - discount;
}

class FreeDelivery extends HTMLElement {
	connectedCallback() {
		this.messageElement = this.querySelector('[data-message]');
		this.onCartChange = this.onCartChange.bind(this);

		document.addEventListener('cart:change', this.onCartChange);
	}

	disconnectedCallback() {
		document.removeEventListener('cart:change', this.onCartChange);
	}

	get formatter() {
		// Built on first use: Shopify.currency is per market, and this element is re-rendered with
		// the rest of the product info whenever that could have changed
		this.currencyFormatter ??= new Intl.NumberFormat(Shopify.locale, {
			style: 'currency',
			currency: Shopify.currency.active,
		});

		return this.currencyFormatter;
	}

	onCartChange(event) {
		const cart = event.detail?.cart;

		if (!cart) {
			return;
		}

		this.setAttribute('cart-total', shippableTotal(cart));
		this.render();
	}

	render() {
		const threshold = Number(this.getAttribute('threshold'));
		const cartTotal = Number(this.getAttribute('cart-total'));
		const itemPrice = Number(this.getAttribute('item-price'));

		if (cartTotal >= threshold) {
			this.messageElement.textContent = this.getAttribute('message-qualified');

			return;
		}

		if (cartTotal + itemPrice >= threshold) {
			this.messageElement.textContent = this.getAttribute('message-qualifying');

			return;
		}

		const remaining = this.formatter.format((threshold - cartTotal - itemPrice) / 100);

		// Replaced through a function, so an amount carrying a currency symbol of its own — a dollar
		// market, say — can't be read as a backreference
		this.messageElement.textContent = this.getAttribute('message-remaining').replace(
			AMOUNT_PLACEHOLDER,
			() => remaining
		);
	}
}

if (!customElements.get('free-delivery')) {
	customElements.define('free-delivery', FreeDelivery);
}
